import DOMPurify from "dompurify"
import React, { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { IPhoneMockup } from "react-device-mockup"
import { getCurrencySymbol } from "../../utils/currencySymbols.js"
import {
	getBracketTextParts,
	getEstimatedArrivalHtml,
} from "./kopyPagesPreviewText.js"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import css from "./KopyPagesPreview.module.css"
import { themeColorPalettes } from "./ThemeColorPalettePicker.jsx"

export const KopyPagesPreview = ({
	colorPalette = null, // Pass one of themeColorPalettes keys
	previewData = {}, // Contains "product" (data obtained from /getProduct), currency, and "metafields" (data obtained from /product-template/generate)
	previewPage = "product",
}) => {
	const { state } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const containerRef = useRef(null)
	const addToCartRef = useRef(null)
	const [showFloatingCart, setShowFloatingCart] = useState(false)

	const colors = themeColorPalettes[colorPalette || "pink"]

	// Convert colors object to CSS variables
	const cssVariables = Object.entries(colors).reduce((acc, [key, value]) => {
		// Convert camelCase to kebab-case for CSS variables
		const cssVarName = key.replace(/([A-Z])/g, "-$1").toLowerCase()
		acc[`--${cssVarName}`] = value
		return acc
	}, {})

	useEffect(() => {
		const container = containerRef.current
		const addToCartButton = addToCartRef.current

		if (!container || !addToCartButton) return

		const observer = new IntersectionObserver(
			([entry]) => {
				// Only show floating button when scrolled PAST the button (button is above viewport)
				// Check if button's bottom is near/above the container's visible area
				// Adding offset to show floating button a bit earlier
				const isScrolledPast =
					!entry.isIntersecting &&
					entry.boundingClientRect.bottom < entry.rootBounds.top
				setShowFloatingCart(isScrolledPast)
			},
			{
				root: container,
				threshold: 0,
			},
		)

		observer.observe(addToCartButton)

		return () => observer.disconnect()
	}, [])

	const storeName = state.storeNames[state.shop] || "My Store"
	const rating = previewData.metafields?.reviews?.averageRating || 0

	const priceFloat = previewData.product?.variants?.length
		? previewData.product?.variants?.reduce(
				(acc, variant) => acc + (parseFloat(variant.price) || 0),
				0,
			) / previewData.product?.variants?.length
		: null
	const compareAtPriceFloat = previewData.product?.variants?.length
		? previewData.product?.variants?.reduce(
				(acc, variant) =>
					acc + (parseFloat(variant.compareAtPrice) || 0),
				0,
			) / previewData.product?.variants?.length
		: null
	const currency = previewData?.currency || "USD"
	const currencySymbol = getCurrencySymbol(currency)
	const mainImageUrl = previewData.product?.images?.[0]?.src || null
	const footerCopy = previewData.metafields?.footer || null
	const emailPlaceholderText =
		previewData.metafields?.ui?.emailPlaceholder ||
		t("components.kopy_pages_preview.email_placeholder")

	return (
		<div style={{ marginBottom: "1rem" }}>
			<IPhoneMockup
				screenWidth={225 * 1.3} // factor has to match --preview-base-size
				transparentNavBar
				statusbarColor={colors.pageBg}
			>
				<div
					ref={containerRef}
					className={css.preview_container}
					style={{
						width: "100%",
						height: "100%",
						background: colors.productSectionBg,
						...cssVariables,
					}}
				>
					<Marquee
						marqueeText={
							previewData.metafields?.ui?.marquee ||
							t("components.kopy_pages_preview.marquee_default")
						}
					/>
					<StickyHeader storeName={storeName} />
					{previewPage === "homepage" ? (
						<HomepageContent
							previewData={previewData}
							storeName={storeName}
							mainImageUrl={mainImageUrl}
							footerCopy={footerCopy}
							emailPlaceholderText={emailPlaceholderText}
						/>
					) : (
						<>
							<ImageCarousel
								imageUrls={
									previewData.product?.images?.map(
										(image) => image.src,
									) || []
								}
							/>
							<ProductTitle
								title={
									previewData.metafields?.productInformation
										?.title || previewData.product?.title
								}
							/>
							<ReviewCount
								rating={rating}
								ratingCount={
									previewData.metafields?.reviews?.count || 0
								}
								reviewsAmountText={
									previewData.metafields?.productInformation
										?.reviewsAmount || ""
								}
							/>
							<PriceSection
								currencySymbol={currencySymbol}
								priceFloat={priceFloat || 0}
								compareAtPriceFloat={compareAtPriceFloat || 0}
							/>
							<ProductDescription
								subtitle={
									previewData.metafields?.productInformation
										?.subtitle || ""
								}
								benefits={
									previewData.metafields?.productInformation
										?.benefits || []
								}
							/>
							<VariantsSection
								option1Name={
									previewData.metafields?.options?.[0]
										?.name ||
									previewData.product?.options?.[0]?.name ||
									""
								}
								option1Values={
									previewData.metafields?.options?.[0]
										?.values ||
									previewData.product?.options?.[0]?.values?.map(
										(v) => ({ name: v, originalName: v }),
									) ||
									[]
								}
								option2Name={
									previewData.metafields?.options?.[1]
										?.name ||
									previewData.product?.options?.[1]?.name ||
									""
								}
								option2Values={
									previewData.metafields?.options?.[1]
										?.values ||
									previewData.product?.options?.[1]?.values?.map(
										(v) => ({ name: v, originalName: v }),
									) ||
									[]
								}
								variants={
									previewData.product?.variants?.map(
										(variant) => ({
											title: variant.title,
											option1: variant.option1,
											option2: variant.option2,
											option3: variant.option3,
											imageSrc:
												variant.imageSrc ||
												variant.featured_image?.src ||
												(variant.image_id
													? previewData.product?.images?.find(
															(image) =>
																image.id ===
																variant.image_id,
														)?.src
													: null),
										}),
									) || []
								}
							/>
							<BundlesSection
								headingText={
									previewData.metafields?.productInformation
										?.bundles?.heading || ""
								}
								bottomText={
									previewData.metafields?.productInformation
										?.bundles?.bottomText || ""
								}
								bundleOfThreeText={
									previewData.metafields?.productInformation
										?.bundles?.bundleOfThree || ""
								}
								bundleOfTwoText={
									previewData.metafields?.productInformation
										?.bundles?.bundleOfTwo || ""
								}
								buyOneText={
									previewData.metafields?.productInformation
										?.bundles?.buyOne || ""
								}
								discountText={
									previewData.metafields?.productInformation
										?.bundles?.discountText || ""
								}
								mostPopularText={
									previewData.metafields?.productInformation
										?.bundles?.mostPopular || ""
								}
								mainImageUrl={mainImageUrl}
								currencySymbol={currencySymbol}
								priceFloat={priceFloat || 0}
								compareAtPriceFloat={compareAtPriceFloat || 0}
								options={
									previewData.metafields?.options ||
									previewData.product?.options?.map((o) => ({
										name: o.name,
										values: o.values.map((v) => ({
											name: v,
											originalName: v,
										})),
									})) ||
									[]
								}
							/>
							<DeliveryEstimationSection
								estimatedArrivalText={
									previewData.metafields?.productInformation
										?.estimatedArrival || ""
								}
								language={
									previewData.metafields?.language || "en"
								}
							/>
							<AddToCartSection
								addToCartRef={addToCartRef}
								returnsText={
									previewData.metafields?.productInformation
										?.USP?.returns || ""
								}
								shippingInformationText={
									previewData.metafields?.productInformation
										?.USP?.shippingInformation || ""
								}
								supportText={
									previewData.metafields?.productInformation
										?.USP?.support || ""
								}
								addToCartButtonText={
									previewData.metafields?.ui
										?.addToCartButton ||
									t(
										"components.kopy_pages_preview.add_to_cart",
									)
								}
							/>
							<HighlightedReviewsSection
								review={
									previewData.metafields?.reviews
										?.items?.[0] || null
								}
							/>
							<ShippingAndReturnInfoSection
								shippingInformationHeadingText={
									previewData.metafields?.productInformation
										?.shippingInformation?.heading || ""
								}
								returnsHeadingText={
									previewData.metafields?.productInformation
										?.returnPolicy?.heading || ""
								}
							/>
							<BenefitsMarquee
								returnsText={
									previewData.metafields?.scrollingUSP
										?.returns || ""
								}
								shippingInformationText={
									previewData.metafields?.scrollingUSP
										?.shippingInformation || ""
								}
								supportText={
									previewData.metafields?.scrollingUSP
										?.support || ""
								}
							/>
							<ImageWithDetailBulletPointsSection
								imageUrl={
									previewData.product?.images?.[1]?.src ||
									previewData.product?.images?.[0]?.src ||
									null
								}
								headingText={
									previewData.metafields?.benefits?.heading ||
									""
								}
								benefits={
									previewData.metafields?.benefits?.items ||
									[]
								}
							/>
							<PurchaseReasonsSection
								headingText={
									previewData.metafields?.reasonsToLove
										?.heading || ""
								}
								reasons={
									previewData.metafields?.reasonsToLove
										?.items || []
								}
							/>
							<ComparisonTableSection
								storeName={storeName}
								headingText={
									previewData.metafields?.comparison
										?.heading || ""
								}
								contentText={
									previewData.metafields?.comparison
										?.content || ""
								}
								items={
									previewData.metafields?.comparison?.items ||
									[]
								}
								othersText={
									previewData.metafields?.comparison
										?.othersText || ""
								}
							/>
							<FaqSection
								headingText={
									previewData.metafields?.faq?.heading || ""
								}
								questions={
									previewData.metafields?.faq?.items || []
								}
							/>
							<ReviewsSection
								reviews={
									previewData.metafields?.reviews?.items || []
								}
								avgRating={rating}
								basedOnText={
									previewData.metafields?.reviews?.basedOn ||
									""
								}
								reviewCount={
									previewData.metafields?.reviews?.count || 0
								}
								headingText={
									previewData.metafields?.reviews?.heading ||
									""
								}
								verifiedBuyerText={
									previewData.metafields?.reviews
										?.verifiedBuyer || ""
								}
								voteText={
									previewData.metafields?.reviews?.voteText ||
									""
								}
								mainImageUrl={mainImageUrl}
								productTitle={
									previewData.metafields?.productInformation
										?.title || previewData.product?.title
								}
							/>
							<EmailSubscriptionSection
								headingText={
									previewData.metafields?.footer?.emailSignup
										?.heading ||
									t(
										"components.kopy_pages_preview.email_heading",
									)
								}
								subheadingText={
									previewData.metafields?.footer?.emailSignup
										?.subheading ||
									t(
										"components.kopy_pages_preview.email_subheading",
									)
								}
								emailPlaceholderText={
									previewData.metafields?.ui
										?.emailPlaceholder ||
									t(
										"components.kopy_pages_preview.email_placeholder",
									)
								}
							/>
							<TrustBadgesSection
								shippingHeading={
									previewData.metafields?.footer?.shopFeatures
										?.shippingInformation?.heading ||
									t(
										"components.kopy_pages_preview.shipping_heading",
									)
								}
								shippingContent={
									previewData.metafields?.footer?.shopFeatures
										?.shippingInformation?.content ||
									t(
										"components.kopy_pages_preview.shipping_content",
									)
								}
								returnHeading={
									previewData.metafields?.footer?.shopFeatures
										?.returnPolicy?.heading ||
									t(
										"components.kopy_pages_preview.return_heading",
									)
								}
								returnContent={
									previewData.metafields?.footer?.shopFeatures
										?.returnPolicy?.content ||
									t(
										"components.kopy_pages_preview.return_content",
									)
								}
								priceMatchHeading={
									previewData.metafields?.footer?.shopFeatures
										?.priceMatchGuarantee?.heading ||
									t(
										"components.kopy_pages_preview.price_match_heading",
									)
								}
								priceMatchContent={
									previewData.metafields?.footer?.shopFeatures
										?.priceMatchGuarantee?.content ||
									t(
										"components.kopy_pages_preview.price_match_content",
									)
								}
								satisfactionHeading={
									previewData.metafields?.footer?.shopFeatures
										?.customerSatisfaction?.heading ||
									t(
										"components.kopy_pages_preview.satisfaction_heading",
									)
								}
								satisfactionContent={
									previewData.metafields?.footer?.shopFeatures
										?.customerSatisfaction?.content ||
									t(
										"components.kopy_pages_preview.satisfaction_content",
									)
								}
							/>
							<FooterSection
								storeName={storeName}
								customerSupportHeading={
									previewData.metafields?.footer
										?.customerSupport?.heading ||
									t(
										"components.kopy_pages_preview.customer_support_heading",
									)
								}
								customerSupportContent={
									previewData.metafields?.footer
										?.customerSupport?.content || ""
								}
							/>
							<FloatingAddToCartButton
								showFloatingCart={showFloatingCart}
								mainImageUrl={mainImageUrl}
								productTitle={
									previewData.metafields?.productInformation
										?.title || previewData.product?.title
								}
								priceFloat={priceFloat || 0}
								compareAtPriceFloat={compareAtPriceFloat || 0}
								currencySymbol={currencySymbol}
								addToCartButtonText={
									previewData.metafields?.ui
										?.addToCartButton ||
									t(
										"components.kopy_pages_preview.add_to_cart",
									)
								}
							/>
						</>
					)}
				</div>
			</IPhoneMockup>
		</div>
	)
}

const renderGradientBracketText = (text = "") =>
	getBracketTextParts(text).map((part, index) =>
		part.highlighted ? (
			<span key={`${part.text}-${index}`}>{part.text}</span>
		) : (
			<React.Fragment key={`${part.text}-${index}`}>
				{part.text}
			</React.Fragment>
		),
	)

const HomepageContent = ({
	previewData,
	storeName,
	mainImageUrl,
	footerCopy,
	emailPlaceholderText,
}) => {
	const hp = previewData.metafields?.homepage
	const heroRatingText = hp?.hero?.ratingText || "4.8 | 1000 Customers"
	const heroHeading =
		hp?.hero?.heading || "Give Them Freedom Without The Fear Of Escape"
	const heroSubheading =
		hp?.hero?.subheading || "Secure comfort for every adventure"
	const heroButtonLabel = hp?.hero?.buttonLabel || "Shop Now"
	const featuredInHeading = hp?.featuredIn?.heading || "As Featured In"
	const secondaryHero = hp?.secondaryHero
	const testimonial = hp?.testimonial
	const comparison = hp?.comparison
	const faq = hp?.faq
	const beforeAfter = hp?.beforeAfter

	return (
		<>
			<div className={css.hp_hero}>
				<div className={css.hp_hero_image}>
					{mainImageUrl ? (
						<img src={mainImageUrl} alt="Hero" />
					) : (
						<div className={css.hp_hero_image_placeholder} />
					)}
				</div>
				<div className={css.hp_hero_content}>
					<div className={css.hp_hero_rating}>
						<span className={css.hp_hero_rating_badge}>
							Excellent
						</span>
						<span className={css.hp_hero_rating_stars}>★★★★★</span>
						<span className={css.hp_hero_rating_text}>
							{heroRatingText}
						</span>
					</div>
					<h1 className={css.hp_hero_heading}>
						{renderGradientBracketText(heroHeading)}
					</h1>
					<p className={css.hp_hero_subheading}>{heroSubheading}</p>
					<button className={css.hp_hero_button}>
						{heroButtonLabel}
					</button>
				</div>
			</div>

			<div className={css.hp_featured_in}>
				<h2 className={css.hp_featured_in_heading}>
					{featuredInHeading}
				</h2>
				<div className={css.hp_featured_in_logos}>
					{Array.from({ length: 3 }).map((_, i) => (
						<span
							key={`logo-${i}`}
							className={css.hp_featured_in_logo}
						>
							LOGO
						</span>
					))}
				</div>
			</div>

			{secondaryHero ? (
				<div className={css.hp_secondary_hero}>
					<div className={css.hp_secondary_hero_images}>
						{(previewData.product?.images || [])
							.slice(0, 4)
							.map((img, i) => (
								<div
									key={`sh-img-${i}`}
									className={css.hp_secondary_hero_image}
								>
									{img?.src ? (
										<img src={img.src} alt="Product" />
									) : null}
								</div>
							))}
					</div>
					<div className={css.hp_secondary_hero_content}>
						<h2 className={css.hp_secondary_hero_heading}>
							{renderGradientBracketText(
								secondaryHero.heading ||
									"Essential gear for safe exploration",
							)}
						</h2>
						<p className={css.hp_secondary_hero_text}>
							{secondaryHero.text || ""}
						</p>
						{(secondaryHero.features || []).map((feature, i) => (
							<div
								key={`feature-${i}`}
								className={css.hp_secondary_hero_feature}
							>
								<CheckCircleIcon />
								<span>{feature}</span>
							</div>
						))}
					</div>
				</div>
			) : null}

			{testimonial ? (
				<div className={css.hp_testimonial}>
					<h2 className={css.hp_testimonial_heading}>
						&ldquo;
						{testimonial.heading || "Customer's Review Title"}
						&rdquo;
					</h2>
					<p className={css.hp_testimonial_text}>
						{testimonial.text || ""}
					</p>
				</div>
			) : null}

			{comparison ? (
				<ComparisonTableSection
					storeName={storeName}
					headingText={comparison.heading || ""}
					contentText={comparison.text || ""}
					items={comparison.items || []}
					othersText={comparison.othersLabel || "Others"}
				/>
			) : null}

			{faq ? (
				<div className={css.hp_faq}>
					<p className={css.hp_faq_caption}>{faq.caption || ""}</p>
					<h2 className={css.hp_faq_heading}>
						{faq.heading || "Frequently Asked Questions"}
					</h2>
					{(faq.items || []).map((item, i) => (
						<div key={`faq-${i}`} className={css.hp_faq_item}>
							<p className={css.hp_faq_question}>
								{item.question || "Collapsible row"}
							</p>
							<svg
								viewBox="0 0 14 14"
								fill="none"
								width="12"
								height="12"
							>
								<path
									d="M11 5.5L7 9.5L3 5.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</div>
					))}
				</div>
			) : null}

			{beforeAfter ? (
				<div className={css.hp_before_after}>
					<h2 className={css.hp_before_after_heading}>
						{renderGradientBracketText(
							beforeAfter.heading || "Our Exciting Results",
						)}
					</h2>
					<p className={css.hp_before_after_text}>
						{beforeAfter.content || ""}
					</p>
					<div className={css.hp_before_after_image}>
						{mainImageUrl ? (
							<img src={mainImageUrl} alt="Before/After" />
						) : null}
						<div className={css.hp_before_after_divider}>
							<div className={css.hp_before_after_handle}>
								|||
							</div>
						</div>
						<div className={css.hp_before_after_labels}>
							<span>{beforeAfter.beforeLabel || "Before"}</span>
							<span>{beforeAfter.afterLabel || "After"}</span>
						</div>
					</div>
					<button className={css.hp_before_after_button}>
						{beforeAfter.buttonLabel || "SHOP NOW"}
					</button>
				</div>
			) : null}

			<TrustBadgesSection
				shippingHeading={
					footerCopy?.shopFeatures?.shippingInformation?.heading
				}
				shippingContent={
					footerCopy?.shopFeatures?.shippingInformation?.content
				}
				returnHeading={footerCopy?.shopFeatures?.returnPolicy?.heading}
				returnContent={footerCopy?.shopFeatures?.returnPolicy?.content}
				priceMatchHeading={
					footerCopy?.shopFeatures?.priceMatchGuarantee?.heading
				}
				priceMatchContent={
					footerCopy?.shopFeatures?.priceMatchGuarantee?.content
				}
				satisfactionHeading={
					footerCopy?.shopFeatures?.customerSatisfaction?.heading
				}
				satisfactionContent={
					footerCopy?.shopFeatures?.customerSatisfaction?.content
				}
			/>
			<EmailSubscriptionSection
				headingText={footerCopy?.emailSignup?.heading}
				subheadingText={footerCopy?.emailSignup?.subheading}
				emailPlaceholderText={emailPlaceholderText}
			/>
			<FooterSection
				storeName={storeName}
				customerSupportHeading={footerCopy?.customerSupport?.heading}
				customerSupportContent={footerCopy?.customerSupport?.content}
			/>
		</>
	)
}

const CheckCircleIcon = () => (
	<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm4.293 7.707l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 111.414-1.414L8.586 10.586l4.293-4.293a1 1 0 011.414 1.414z"
			fill="currentColor"
		/>
	</svg>
)

const MarqueeIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		className="icon icon-accordion icon-truck"
		viewBox="0 0 20 20"
	>
		<path d="M0 3.752c0-.277.224-.502.5-.502h10.147a.5.5 0 0 1 .5.502v9.91l-.595 1.003H7.223V13.66h2.923V4.253H1.002v9.408h1.682v1.004H.501A.5.5 0 0 1 0 14.163zm18.999 4.332-7.851-1.975V5.075l8.473 2.131a.5.5 0 0 1 .379.487v6.47a.5.5 0 0 1-.5.502h-2.193v-1.003H19zm-7.851 5.578h2.196v1.003h-2.792z"></path>
		<path d="M7.713 14.185a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562m12.368-1.562a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562"></path>
	</svg>
)

const Marquee = ({ marqueeText = "FREE SHIPPING ON ALL ORDERS" }) => {
	return (
		<div
			style={{
				width: "100%",
				borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
			}}
		>
			<div className={css.marquee}>
				<div className={css.marquee_item}>
					<MarqueeIcon />
					<p>{marqueeText}</p>
				</div>
				<div className={css.marquee_item}>
					<MarqueeIcon />
					<p>{marqueeText}</p>
				</div>
				<div className={css.marquee_item}>
					<MarqueeIcon />
					<p>{marqueeText}</p>
				</div>
			</div>
		</div>
	)
}

const StickyHeader = ({ storeName }) => {
	return (
		<div className={css.sticky_header}>
			<div className={css.sticky_header_left}>
				<svg
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M1 3.5H13"
						stroke="currentColor"
						strokeWidth="var(--icon-stroke-width)"
						strokeLinecap="round"
					></path>
					<path
						d="M1 10.5H13"
						stroke="currentColor"
						strokeWidth="var(--icon-stroke-width)"
						strokeLinecap="round"
					></path>
				</svg>
			</div>

			<div className={css.sticky_header_heading}>
				<p>{storeName}</p>
			</div>

			<div className={css.sticky_header_right}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="none"
				>
					<circle
						cx="9"
						cy="9"
						r="4.75"
						stroke="currentColor"
						strokeWidth="var(--icon-stroke-width)"
					></circle>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="var(--icon-stroke-width)"
						d="m12.5 12.5 3.25 3.25"
					></path>
				</svg>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 20 20"
					fill="none"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="var(--icon-stroke-width)"
						d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"
					></path>
				</svg>
			</div>
		</div>
	)
}

const ImageCarousel = ({ imageUrls = [] }) => {
	return (
		<div className={css.image_carousel}>
			<div className={css.image_carousel_main_image}>
				<img src={imageUrls[0]} alt="Product" />
			</div>
			<div className={css.image_carousel_items}>
				<svg
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M11 5.5L7 9.5L3 5.5"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
					></path>
				</svg>
				{imageUrls.slice(0, 6).map((imageUrl, i) => (
					<div
						className={css.image_carousel_item}
						key={`image-carousel-item-${i}`}
					>
						<img src={imageUrl} alt="Product" />
					</div>
				))}
				<svg
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M11 5.5L7 9.5L3 5.5"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
					></path>
				</svg>
			</div>
		</div>
	)
}

const ProductTitle = ({ title }) => {
	return (
		<div className={css.product_title}>
			<h1>{title}</h1>
		</div>
	)
}

const ReviewCount = ({ rating, ratingCount, reviewsAmountText = "" }) => {
	const { t } = useTranslation()
	const template =
		reviewsAmountText || t("components.kopy_pages_preview.reviews_count")
	const displayText = template.replace("<amount>", String(ratingCount || 97))
	return (
		<div className={css.review_count}>
			<StarRating rating={rating} size={10} />
			<p>{displayText}</p>
		</div>
	)
}

const PriceSection = ({ currencySymbol, priceFloat, compareAtPriceFloat }) => {
	const discountPercentage =
		compareAtPriceFloat && priceFloat
			? ((compareAtPriceFloat - priceFloat) / compareAtPriceFloat) * 100
			: null
	return (
		<div className={css.price_section}>
			<p className={css.price_section_price}>
				{currencySymbol}
				{priceFloat.toFixed(2)}
			</p>
			{compareAtPriceFloat ? (
				<p className={css.price_section_compare_at_price}>
					{currencySymbol}
					{compareAtPriceFloat.toFixed(2)}
				</p>
			) : null}
			{discountPercentage ? (
				<div className={css.price_section_savings}>
					- {discountPercentage.toFixed(0)}%
				</div>
			) : null}
		</div>
	)
}

const ProductDescription = ({ subtitle = "", benefits = [] }) => {
	return (
		<div className={css.product_description}>
			<p>{subtitle}</p>
			{benefits.map((benefit, i) => (
				<p key={`benefit-${i}`}>✓ {benefit}</p>
			))}
		</div>
	)
}

const VariantsSection = ({
	option1Name = "",
	option1Values = [], // [{ name, originalName, image? }]
	variants = [],
	option2Name = "",
	option2Values = [], // [{ name, originalName, image? }]
}) => {
	if (variants.length === 0 || !option1Name || option1Values.length === 0)
		return null

	const getOptionImage = (value, optionKey) => {
		if (value.image) return value.image
		return variants.find((v) => v[optionKey] === value.originalName)
			?.imageSrc
	}

	return (
		<div className={css.variants_section}>
			<p className={css.variants_section_title}>
				{option1Name}: {option1Values[0]?.name || ""}
			</p>
			<div className={css.variants_section_variants}>
				{option1Values.map((value, i) => {
					const imageSrc = getOptionImage(value, "option1")
					return (
						<div
							key={`option1-value-${i}`}
							className={
								imageSrc && i === 0
									? css.variants_section_image_selected
									: ""
							}
						>
							{imageSrc ? (
								<img src={imageSrc} alt={value.name} />
							) : (
								<p
									className={
										i === 0
											? css.variants_section_variant_selected
											: ""
									}
								>
									{value.name}
								</p>
							)}
						</div>
					)
				})}
			</div>
			{option2Name && option2Values.length > 0 && (
				<>
					<p className={css.variants_section_title}>
						{option2Name}: {option2Values[0]?.name || ""}
					</p>
					<div className={css.variants_section_variants}>
						{option2Values.map((value, i) => {
							const imageSrc = getOptionImage(value, "option2")
							return (
								<div
									key={`option2-value-${i}`}
									className={
										imageSrc && i === 0
											? css.variants_section_image_selected
											: ""
									}
								>
									{imageSrc ? (
										<img src={imageSrc} alt={value.name} />
									) : (
										<p
											className={
												i === 0
													? css.variants_section_variant_selected
													: ""
											}
										>
											{value.name}
										</p>
									)}
								</div>
							)
						})}
					</div>
				</>
			)}
		</div>
	)
}

const BundlesSection = ({
	headingText = "",
	bottomText = "",
	bundleOfThreeText = "",
	bundleOfTwoText = "",
	buyOneText = "",
	discountText = "",
	mostPopularText = "",
	options = [],
	mainImageUrl,
	currencySymbol,
	priceFloat,
	compareAtPriceFloat,
}) => {
	const price2Float = priceFloat * 2 * 0.9
	const price3Float = priceFloat * 3 * 0.75
	const price2CompareAtPriceFloat = compareAtPriceFloat
		? compareAtPriceFloat * 2
		: null
	const price3CompareAtPriceFloat = compareAtPriceFloat
		? compareAtPriceFloat * 3
		: null
	return (
		<div className={css.bundles_section}>
			<h2>{headingText}</h2>

			<div
				className={css.bundles_section_bundle}
				style={{ backgroundColor: "#fff" }}
			>
				<div className={css.bundles_section_bundle_content}>
					<img src={mainImageUrl} alt="Product" />

					<div>
						<div
							className={css.bundles_section_bundle_content_item}
						>
							<div className={css.bundles_section_bundle_radio}>
								<div
									style={{
										backgroundColor: "var(--page-text)",
										borderRadius: "100%",
										width: "100%",
										height: "100%",
									}}
								/>
							</div>

							<p className={css.bundles_section_bundle_title}>
								{buyOneText}
							</p>
						</div>

						<div className={css.bundles_section_bundle_discount}>
							<p>{discountText.replace("[%]", "0%")}</p>
						</div>
					</div>

					<div className={css.bundles_section_bundle_price_container}>
						<p className={css.bundles_section_bundle_price}>
							{currencySymbol}
							{priceFloat.toFixed(2)}
						</p>
						{compareAtPriceFloat ? (
							<p
								className={
									css.bundles_section_bundle_compare_at_price
								}
							>
								{currencySymbol}
								{compareAtPriceFloat.toFixed(2)}
							</p>
						) : null}
					</div>
				</div>

				<div className={css.bundles_section_bundle_variants_container}>
					<p>1.</p>
					{options.map((option, i) => (
						<div
							key={`option-${i}`}
							className={css.bundles_section_bundle_variant}
						>
							<p>{option.values[0]?.name || "Default"}</p>
							<svg
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11 5.5L7 9.5L3 5.5"
									stroke="currentColor"
									strokeWidth="var(--icon-stroke-width)"
									strokeLinecap="round"
									strokeLinejoin="round"
								></path>
							</svg>
						</div>
					))}
				</div>
			</div>

			<div
				className={css.bundles_section_bundle}
				style={{ borderColor: "var(--page-text-semi-transparent)" }}
			>
				{/* Most Popular Badge */}
				<div
					className={
						css.bundles_section_bundle_most_popular_container
					}
				>
					<div
						className={
							css.bundles_section_bundle_most_popular_badge
						}
					>
						<p>{mostPopularText}</p>
					</div>
				</div>

				<div className={css.bundles_section_bundle_content}>
					<img src={mainImageUrl} alt="Product" />

					<div>
						<div
							className={css.bundles_section_bundle_content_item}
						>
							<div
								className={css.bundles_section_bundle_radio}
								style={{
									borderColor:
										"var(--page-text-semi-transparent)",
								}}
							></div>
							<p className={css.bundles_section_bundle_title}>
								{bundleOfTwoText}
							</p>
						</div>

						<div className={css.bundles_section_bundle_discount}>
							<p>{discountText.replace("[%]", "10%")}</p>
						</div>
					</div>

					<div className={css.bundles_section_bundle_price_container}>
						<p className={css.bundles_section_bundle_price}>
							{currencySymbol}
							{price2Float.toFixed(2)}
						</p>
						{price2CompareAtPriceFloat ? (
							<p
								className={
									css.bundles_section_bundle_compare_at_price
								}
							>
								{currencySymbol}
								{price2CompareAtPriceFloat.toFixed(2)}
							</p>
						) : null}
					</div>
				</div>

				<div className={css.bundles_section_bundle_bottom_text}>
					{bottomText}
				</div>
			</div>

			<div
				className={css.bundles_section_bundle}
				style={{ borderColor: "var(--page-text-semi-transparent)" }}
			>
				<div className={css.bundles_section_bundle_content}>
					<img src={mainImageUrl} alt="Product" />

					<div>
						<div
							className={css.bundles_section_bundle_content_item}
						>
							<div
								className={css.bundles_section_bundle_radio}
								style={{
									borderColor:
										"var(--page-text-semi-transparent)",
								}}
							></div>

							<p className={css.bundles_section_bundle_title}>
								{bundleOfThreeText}
							</p>
						</div>

						<div className={css.bundles_section_bundle_discount}>
							<p>{discountText.replace("[%]", "25%")}</p>
						</div>
					</div>

					<div className={css.bundles_section_bundle_price_container}>
						<p className={css.bundles_section_bundle_price}>
							{currencySymbol}
							{price3Float.toFixed(2)}
						</p>
						{price3CompareAtPriceFloat ? (
							<p
								className={
									css.bundles_section_bundle_compare_at_price
								}
							>
								{currencySymbol}
								{price3CompareAtPriceFloat.toFixed(2)}
							</p>
						) : null}
					</div>
				</div>
			</div>
		</div>
	)
}

const DeliveryEstimationSection = ({
	estimatedArrivalText = "",
	language = "en",
}) => {
	const estimatedDeliveryDate = new Date(
		Date.now() + 7 * 24 * 60 * 60 * 1000,
	).toLocaleDateString(language, {
		weekday: "short",
		month: "short",
		day: "numeric",
	})
	const estimatedArrivalHtml = DOMPurify.sanitize(
		getEstimatedArrivalHtml(estimatedArrivalText, estimatedDeliveryDate),
		{
			ALLOWED_TAGS: ["strong"],
			ALLOWED_ATTR: [],
		},
	)

	return (
		<div className={css.delivery_estimation_section}>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 20 20"
			>
				<g
					clipPath="url(#a)"
					stroke="currentColor"
					strokeMiterlimit="10"
					strokeWidth="1.2"
				>
					<path d="m15.367 14.242h2.9633v-4.8485l-2.3706-1.2121-1.778-4.8484h-5.334v10.909h1.778"></path>
					<path
						d="m13 16.666c1.3093 0 2.3706-1.0854 2.3706-2.4242 0-1.3389-1.0613-2.4242-2.3706-2.4242s-2.3707 1.0853-2.3707 2.4242c0 1.3388 1.0614 2.4242 2.3707 2.4242z"
						strokeLinecap="square"
					></path>
					<path
						d="m11.223 5.7568v2.4242h2.3706"
						strokeLinecap="square"
					></path>
					<path
						d="m6.4814 3.3332h-5.9267"
						strokeLinecap="square"
					></path>
					<path
						d="m6.4807 6.3628h-4.1487"
						strokeLinecap="square"
					></path>
					<path d="m6.48 9.394h-2.3706" strokeLinecap="square"></path>
				</g>
				<defs>
					<clipPath id="a">
						<rect width="100%" height="100%" fill="none"></rect>
					</clipPath>
				</defs>
			</svg>
			<h2
				dangerouslySetInnerHTML={{
					__html: estimatedArrivalHtml,
				}}
			/>
		</div>
	)
}

const AddToCartSection = ({
	addToCartRef,
	returnsText = "",
	shippingInformationText = "",
	supportText = "",
	addToCartButtonText = "ADD TO CART",
}) => {
	return (
		<div className={css.add_to_cart_section}>
			<button
				ref={addToCartRef}
				className={css.add_to_cart_section_button}
			>
				{addToCartButtonText}
			</button>

			<div className={css.add_to_cart_section_payment_methods}>
				<svg
					viewBox="0 0 38 24"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-labelledby="pi-visa"
				>
					<title id="pi-visa">Visa</title>
					<path
						opacity=".07"
						d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"
					></path>
					<path
						fill="#fff"
						d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"
					></path>
					<path
						d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2zm-13.4-.3l.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2M5 8.2c0-.1.2-.2.3-.2h3.4c.5 0 .9.3 1 .8l.9 4.4c0 .1 0 .1.1.2 0-.1.1-.1.1-.1l2.1-5.1c-.1-.1 0-.2.1-.2h2.1c0 .1 0 .1-.1.2l-3.1 7.3c-.1.2-.1.3-.2.4-.1.1-.3 0-.5 0H9.7c-.1 0-.2 0-.2-.2L7.9 9.5c-.2-.2-.5-.5-.9-.6-.6-.3-1.7-.5-1.9-.5L5 8.2z"
						fill="#142688"
					></path>
				</svg>

				<svg
					viewBox="0 0 38 24"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-labelledby="pi-master"
				>
					<title id="pi-master">Mastercard</title>
					<path
						opacity=".07"
						d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"
					></path>
					<path
						fill="#fff"
						d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"
					></path>
					<circle fill="#EB001B" cx="15" cy="12" r="7"></circle>
					<circle fill="#F79E1B" cx="23" cy="12" r="7"></circle>
					<path
						fill="#FF5F00"
						d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7s1.2 4.5 3 5.7c1.8-1.2 3-3.3 3-5.7z"
					></path>
				</svg>

				<svg
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-labelledby="pi-american_express"
					viewBox="0 0 38 24"
				>
					<title id="pi-american_express">American Express</title>
					<path
						fill="#000"
						d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3Z"
						opacity=".07"
					></path>
					<path
						fill="#006FCF"
						d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32Z"
					></path>
					<path
						fill="#FFF"
						d="M22.012 19.936v-8.421L37 11.528v2.326l-1.732 1.852L37 17.573v2.375h-2.766l-1.47-1.622-1.46 1.628-9.292-.02Z"
					></path>
					<path
						fill="#006FCF"
						d="M23.013 19.012v-6.57h5.572v1.513h-3.768v1.028h3.678v1.488h-3.678v1.01h3.768v1.531h-5.572Z"
					></path>
					<path
						fill="#006FCF"
						d="m28.557 19.012 3.083-3.289-3.083-3.282h2.386l1.884 2.083 1.89-2.082H37v.051l-3.017 3.23L37 18.92v.093h-2.307l-1.917-2.103-1.898 2.104h-2.321Z"
					></path>
					<path
						fill="#FFF"
						d="M22.71 4.04h3.614l1.269 2.881V4.04h4.46l.77 2.159.771-2.159H37v8.421H19l3.71-8.421Z"
					></path>
					<path
						fill="#006FCF"
						d="m23.395 4.955-2.916 6.566h2l.55-1.315h2.98l.55 1.315h2.05l-2.904-6.566h-2.31Zm.25 3.777.875-2.09.873 2.09h-1.748Z"
					></path>
					<path
						fill="#006FCF"
						d="M28.581 11.52V4.953l2.811.01L32.84 9l1.456-4.046H37v6.565l-1.74.016v-4.51l-1.644 4.494h-1.59L30.35 7.01v4.51h-1.768Z"
					></path>
				</svg>

				<svg
					viewBox="0 0 38 24"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					aria-labelledby="pi-paypal"
				>
					<title id="pi-paypal">PayPal</title>
					<path
						opacity=".07"
						d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"
					></path>
					<path
						fill="#fff"
						d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"
					></path>
					<path
						fill="#003087"
						d="M23.9 8.3c.2-1 0-1.7-.6-2.3-.6-.7-1.7-1-3.1-1h-4.1c-.3 0-.5.2-.6.5L14 15.6c0 .2.1.4.3.4H17l.4-3.4 1.8-2.2 4.7-2.1z"
					></path>
					<path
						fill="#3086C8"
						d="M23.9 8.3l-.2.2c-.5 2.8-2.2 3.8-4.6 3.8H18c-.3 0-.5.2-.6.5l-.6 3.9-.2 1c0 .2.1.4.3.4H19c.3 0 .5-.2.5-.4v-.1l.4-2.4v-.1c0-.2.3-.4.5-.4h.3c2.1 0 3.7-.8 4.1-3.2.2-1 .1-1.8-.4-2.4-.1-.5-.3-.7-.5-.8z"
					></path>
					<path
						fill="#012169"
						d="M23.3 8.1c-.1-.1-.2-.1-.3-.1-.1 0-.2 0-.3-.1-.3-.1-.7-.1-1.1-.1h-3c-.1 0-.2 0-.2.1-.2.1-.3.2-.3.4l-.7 4.4v.1c0-.3.3-.5.6-.5h1.3c2.5 0 4.1-1 4.6-3.8v-.2c-.1-.1-.3-.2-.5-.2h-.1z"
					></path>
				</svg>

				<svg
					version="1.1"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					x="0"
					y="0"
					viewBox="0 0 165.521 105.965"
					xmlSpace="preserve"
					aria-labelledby="pi-apple_pay"
				>
					<title id="pi-apple_pay">Apple Pay</title>
					<path
						fill="#000"
						d="M150.698 0H14.823c-.566 0-1.133 0-1.698.003-.477.004-.953.009-1.43.022-1.039.028-2.087.09-3.113.274a10.51 10.51 0 0 0-2.958.975 9.932 9.932 0 0 0-4.35 4.35 10.463 10.463 0 0 0-.975 2.96C.113 9.611.052 10.658.024 11.696a70.22 70.22 0 0 0-.022 1.43C0 13.69 0 14.256 0 14.823v76.318c0 .567 0 1.132.002 1.699.003.476.009.953.022 1.43.028 1.036.09 2.084.275 3.11a10.46 10.46 0 0 0 .974 2.96 9.897 9.897 0 0 0 1.83 2.52 9.874 9.874 0 0 0 2.52 1.83c.947.483 1.917.79 2.96.977 1.025.183 2.073.245 3.112.273.477.011.953.017 1.43.02.565.004 1.132.004 1.698.004h135.875c.565 0 1.132 0 1.697-.004.476-.002.952-.009 1.431-.02 1.037-.028 2.085-.09 3.113-.273a10.478 10.478 0 0 0 2.958-.977 9.955 9.955 0 0 0 4.35-4.35c.483-.947.789-1.917.974-2.96.186-1.026.246-2.074.274-3.11.013-.477.02-.954.022-1.43.004-.567.004-1.132.004-1.699V14.824c0-.567 0-1.133-.004-1.699a63.067 63.067 0 0 0-.022-1.429c-.028-1.038-.088-2.085-.274-3.112a10.4 10.4 0 0 0-.974-2.96 9.94 9.94 0 0 0-4.35-4.35A10.52 10.52 0 0 0 156.939.3c-1.028-.185-2.076-.246-3.113-.274a71.417 71.417 0 0 0-1.431-.022C151.83 0 151.263 0 150.698 0z"
					></path>
					<path
						fill="#FFF"
						d="M150.698 3.532l1.672.003c.452.003.905.008 1.36.02.793.022 1.719.065 2.583.22.75.135 1.38.34 1.984.648a6.392 6.392 0 0 1 2.804 2.807c.306.6.51 1.226.645 1.983.154.854.197 1.783.218 2.58.013.45.019.9.02 1.36.005.557.005 1.113.005 1.671v76.318c0 .558 0 1.114-.004 1.682-.002.45-.008.9-.02 1.35-.022.796-.065 1.725-.221 2.589a6.855 6.855 0 0 1-.645 1.975 6.397 6.397 0 0 1-2.808 2.807c-.6.306-1.228.511-1.971.645-.881.157-1.847.2-2.574.22-.457.01-.912.017-1.379.019-.555.004-1.113.004-1.669.004H14.801c-.55 0-1.1 0-1.66-.004a74.993 74.993 0 0 1-1.35-.018c-.744-.02-1.71-.064-2.584-.22a6.938 6.938 0 0 1-1.986-.65 6.337 6.337 0 0 1-1.622-1.18 6.355 6.355 0 0 1-1.178-1.623 6.935 6.935 0 0 1-.646-1.985c-.156-.863-.2-1.788-.22-2.578a66.088 66.088 0 0 1-.02-1.355l-.003-1.327V14.474l.002-1.325a66.7 66.7 0 0 1 .02-1.357c.022-.792.065-1.717.222-2.587a6.924 6.924 0 0 1 .646-1.981c.304-.598.7-1.144 1.18-1.623a6.386 6.386 0 0 1 1.624-1.18 6.96 6.96 0 0 1 1.98-.646c.865-.155 1.792-.198 2.586-.22.452-.012.905-.017 1.354-.02l1.677-.003h135.875"
					></path>
					<g>
						<g>
							<path
								fill="#000"
								d="M43.508 35.77c1.404-1.755 2.356-4.112 2.105-6.52-2.054.102-4.56 1.355-6.012 3.112-1.303 1.504-2.456 3.959-2.156 6.266 2.306.2 4.61-1.152 6.063-2.858"
							></path>
							<path
								fill="#000"
								d="M45.587 39.079c-3.35-.2-6.196 1.9-7.795 1.9-1.6 0-4.049-1.8-6.698-1.751-3.447.05-6.645 2-8.395 5.1-3.598 6.2-.95 15.4 2.55 20.45 1.699 2.5 3.747 5.25 6.445 5.151 2.55-.1 3.549-1.65 6.647-1.65 3.097 0 3.997 1.65 6.696 1.6 2.798-.05 4.548-2.5 6.247-5 1.95-2.85 2.747-5.6 2.797-5.75-.05-.05-5.396-2.101-5.446-8.251-.05-5.15 4.198-7.6 4.398-7.751-2.399-3.548-6.147-3.948-7.447-4.048"
							></path>
						</g>
						<g>
							<path
								fill="#000"
								d="M78.973 32.11c7.278 0 12.347 5.017 12.347 12.321 0 7.33-5.173 12.373-12.529 12.373h-8.058V69.62h-5.822V32.11h14.062zm-8.24 19.807h6.68c5.07 0 7.954-2.729 7.954-7.46 0-4.73-2.885-7.434-7.928-7.434h-6.706v14.894z"
							></path>
							<path
								fill="#000"
								d="M92.764 61.847c0-4.809 3.665-7.564 10.423-7.98l7.252-.442v-2.08c0-3.04-2.001-4.704-5.562-4.704-2.938 0-5.07 1.507-5.51 3.82h-5.252c.157-4.86 4.731-8.395 10.918-8.395 6.654 0 10.995 3.483 10.995 8.89v18.663h-5.38v-4.497h-.13c-1.534 2.937-4.914 4.782-8.579 4.782-5.406 0-9.175-3.222-9.175-8.057zm17.675-2.417v-2.106l-6.472.416c-3.64.234-5.536 1.585-5.536 3.95 0 2.288 1.975 3.77 5.068 3.77 3.95 0 6.94-2.522 6.94-6.03z"
							></path>
							<path
								fill="#000"
								d="M120.975 79.652v-4.496c.364.051 1.247.103 1.715.103 2.573 0 4.029-1.09 4.913-3.899l.52-1.663-9.852-27.293h6.082l6.863 22.146h.13l6.862-22.146h5.927l-10.216 28.67c-2.34 6.577-5.017 8.735-10.683 8.735-.442 0-1.872-.052-2.261-.157z"
							></path>
						</g>
					</g>
				</svg>

				<svg
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					viewBox="0 0 38 24"
					aria-labelledby="pi-shopify_pay"
				>
					<title id="pi-shopify_pay">Shop Pay</title>
					<path
						opacity=".07"
						d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"
						fill="#000"
					></path>
					<path
						d="M35.889 0C37.05 0 38 .982 38 2.182v19.636c0 1.2-.95 2.182-2.111 2.182H2.11C.95 24 0 23.018 0 21.818V2.182C0 .982.95 0 2.111 0H35.89z"
						fill="#5A31F4"
					></path>
					<path
						d="M9.35 11.368c-1.017-.223-1.47-.31-1.47-.705 0-.372.306-.558.92-.558.54 0 .934.238 1.225.704a.079.079 0 00.104.03l1.146-.584a.082.082 0 00.032-.114c-.475-.831-1.353-1.286-2.51-1.286-1.52 0-2.464.755-2.464 1.956 0 1.275 1.15 1.597 2.17 1.82 1.02.222 1.474.31 1.474.705 0 .396-.332.582-.993.582-.612 0-1.065-.282-1.34-.83a.08.08 0 00-.107-.035l-1.143.57a.083.083 0 00-.036.111c.454.92 1.384 1.437 2.627 1.437 1.583 0 2.539-.742 2.539-1.98s-1.155-1.598-2.173-1.82v-.003zM15.49 8.855c-.65 0-1.224.232-1.636.646a.04.04 0 01-.069-.03v-2.64a.08.08 0 00-.08-.081H12.27a.08.08 0 00-.08.082v8.194a.08.08 0 00.08.082h1.433a.08.08 0 00.081-.082v-3.594c0-.695.528-1.227 1.239-1.227.71 0 1.226.521 1.226 1.227v3.594a.08.08 0 00.081.082h1.433a.08.08 0 00.081-.082v-3.594c0-1.51-.981-2.577-2.355-2.577zM20.753 8.62c-.778 0-1.507.24-2.03.588a.082.082 0 00-.027.109l.632 1.088a.08.08 0 00.11.03 2.5 2.5 0 011.318-.366c1.25 0 2.17.891 2.17 2.068 0 1.003-.736 1.745-1.669 1.745-.76 0-1.288-.446-1.288-1.077 0-.361.152-.657.548-.866a.08.08 0 00.032-.113l-.596-1.018a.08.08 0 00-.098-.035c-.799.299-1.359 1.018-1.359 1.984 0 1.46 1.152 2.55 2.76 2.55 1.877 0 3.227-1.313 3.227-3.195 0-2.018-1.57-3.492-3.73-3.492zM28.675 8.843c-.724 0-1.373.27-1.845.746-.026.027-.069.007-.069-.029v-.572a.08.08 0 00-.08-.082h-1.397a.08.08 0 00-.08.082v8.182a.08.08 0 00.08.081h1.433a.08.08 0 00.081-.081v-2.683c0-.036.043-.054.069-.03a2.6 2.6 0 001.808.7c1.682 0 2.993-1.373 2.993-3.157s-1.313-3.157-2.993-3.157zm-.271 4.929c-.956 0-1.681-.768-1.681-1.783s.723-1.783 1.681-1.783c.958 0 1.68.755 1.68 1.783 0 1.027-.713 1.783-1.681 1.783h.001z"
						fill="#fff"
					></path>
				</svg>
			</div>

			<div className={css.add_to_cart_section_benefits}>
				<div className={css.add_to_cart_section_benefits_benefit}>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
						<path d="M0 3.752c0-.277.224-.502.5-.502h10.147a.5.5 0 0 1 .5.502v9.91l-.595 1.003H7.223V13.66h2.923V4.253H1.002v9.408h1.682v1.004H.501A.5.5 0 0 1 0 14.163zm18.999 4.332-7.851-1.975V5.075l8.473 2.131a.5.5 0 0 1 .379.487v6.47a.5.5 0 0 1-.5.502h-2.193v-1.003H19zm-7.851 5.578h2.196v1.003h-2.792z"></path>
						<path d="M7.713 14.185a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562m12.368-1.562a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562"></path>
					</svg>
					<p>{shippingInformationText}</p>
				</div>

				<div className={css.add_to_cart_section_benefits_benefit}>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
						<path d="M5.8 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0M9.23 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0M12.66 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0"></path>
						<path
							fillRule="evenodd"
							d="m16.645 17.204-.008-.004a19 19 0 0 1-.767-.423c-.645-.367-1.263-.72-1.442-.598a7.472 7.472 0 1 1 2.05-2.082c-.134.202.192.843.533 1.513.147.29.297.584.414.85l.006.014c.193.443.293.808.136.944-.128.136-.488.006-.922-.214m1.614.936c-.38.372-.857.345-1.046.321a2.5 2.5 0 0 1-.626-.181 11 11 0 0 1-1.081-.56l-.132-.074c-.29-.166-.538-.307-.744-.408a8.472 8.472 0 1 1 2.877-2.902l.008.02c.092.22.226.483.385.795l.084.165c.178.353.388.774.516 1.146.063.181.131.42.143.669.01.225-.02.668-.384 1.009"
						></path>
					</svg>
					<p>{supportText}</p>
				</div>

				<div className={css.add_to_cart_section_benefits_benefit}>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
						<path d="M11.571 1.059c0-.309.249-.559.556-.559h1.33C17.07.5 20 3.453 20 7.09s-2.93 6.589-6.543 6.589L1.9 13.71l-.591-.59.59-.572 11.558.013c2.997 0 5.432-2.451 5.432-5.472 0-3.02-2.435-5.471-5.432-5.471h-1.33a.557.557 0 0 1-.556-.56"></path>
						<path d="M6.003 7.007a.553.553 0 0 1 .786.011.56.56 0 0 1-.012.79zM1.31 13.12l5.43 5.426a.56.56 0 0 1 0 .79.553.553 0 0 1-.785 0L.162 13.503a.56.56 0 0 1 .007-.796l3.035-2.965 2.8-2.735.773.801-2.798 2.736c-1.025 1-2.108 2.027-2.67 2.576"></path>
					</svg>
					<p>{returnsText}</p>
				</div>
			</div>
		</div>
	)
}

const FloatingAddToCartButton = ({
	showFloatingCart,
	mainImageUrl,
	productTitle,
	priceFloat,
	compareAtPriceFloat,
	currencySymbol,
	addToCartButtonText = "ADD TO CART",
}) => {
	return (
		<div
			className={`${css.floating_add_to_cart} ${showFloatingCart ? css.floating_add_to_cart_visible : ""}`}
		>
			<img
				src={mainImageUrl}
				className={css.floating_add_to_cart_image}
			/>

			<div className={css.floating_add_to_cart_content}>
				<p className={css.floating_add_to_cart_title}>{productTitle}</p>
				<div className={css.floating_add_to_cart_price_container}>
					<p className={css.floating_add_to_cart_price}>
						{currencySymbol}
						{priceFloat.toFixed(2)}
					</p>
					{compareAtPriceFloat ? (
						<p
							className={
								css.floating_add_to_cart_compare_at_price
							}
						>
							{currencySymbol}
							{compareAtPriceFloat.toFixed(2)}
						</p>
					) : null}
				</div>
				<button className={css.floating_add_to_cart_button}>
					{addToCartButtonText}
				</button>
			</div>
		</div>
	)
}

const HighlightedReviewsSection = ({ review = null }) => {
	if (!review) return ""

	return (
		<div className={css.highlighted_reviews_section}>
			<img
				src={review.authorPhotoSrc}
				className={css.highlighted_reviews_section_author_photo}
			/>
			<div>
				<p className={css.highlighted_reviews_section_content}>
					{review.content}
				</p>
				<p className={css.highlighted_reviews_section_author_name}>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14">
						<path d="M6.506.28a1.49 1.49 0 011.806.051l.107.096.808.793c.137.134.307.226.492.267l.141.021 1.118.081a1.49 1.49 0 011.353 1.214l.019.134.109 1.125c.018.2.095.39.22.545l.08.09.808.792c.474.475.57 1.202.249 1.779l-.075.121-.627.936a1.022 1.022 0 00-.177.532l.004.143.109 1.13a1.492 1.492 0 01-.949 1.54l-.136.045-1.088.3a1.018 1.018 0 00-.494.312l-.073.094-.633.932a1.49 1.49 0 01-1.715.57l-.133-.053-1.029-.468a1.019 1.019 0 00-.554-.082l-.14.028-1.094.317c-.65.18-1.332-.1-1.674-.667l-.068-.126-.486-1.027a1.019 1.019 0 00-.394-.433l-.106-.055-1.029-.468a1.494 1.494 0 01-.858-1.588l.029-.14.283-1.1A1.01 1.01 0 00.693 7.5l-.052-.133-.493-1.019A1.493 1.493 0 01.51 4.576l.113-.089.918-.65c.165-.117.291-.28.363-.467l.037-.115.287-1.097a1.49 1.49 0 011.418-1.121l.144.004 1.124.084c.19.015.38-.025.548-.112l.122-.075.922-.658zm3.756 5.105c.686-.682-.401-1.526-.947-.98l-.334.344-2.872 3.05-.57-.612-.327-.34-.359-.367-.123-.124a.687.687 0 10-.947.985l.45.46 1.078 1.133.798.851 3.29-3.502.796-.83.067-.068z"></path>
					</svg>
					{review.authorName}
				</p>
			</div>
		</div>
	)
}

const ShippingAndReturnInfoSection = ({
	shippingInformationHeadingText = "",
	returnsHeadingText = "",
}) => {
	return (
		<div className={css.shipping_and_return_info_section}>
			<div>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
					<path d="M0 3.752c0-.277.224-.502.5-.502h10.147a.5.5 0 0 1 .5.502v9.91l-.595 1.003H7.223V13.66h2.923V4.253H1.002v9.408h1.682v1.004H.501A.5.5 0 0 1 0 14.163zm18.999 4.332-7.851-1.975V5.075l8.473 2.131a.5.5 0 0 1 .379.487v6.47a.5.5 0 0 1-.5.502h-2.193v-1.003H19zm-7.851 5.578h2.196v1.003h-2.792z"></path>
					<path d="M7.713 14.185a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562m12.368-1.562a2.56 2.56 0 0 1-2.56 2.565 2.56 2.56 0 0 1-2.56-2.565 2.56 2.56 0 0 1 2.56-2.564 2.56 2.56 0 0 1 2.56 2.564m-2.56 1.562a1.56 1.56 0 0 0 1.558-1.562 1.56 1.56 0 0 0-1.559-1.56 1.56 1.56 0 0 0-1.558 1.56 1.56 1.56 0 0 0 1.558 1.562"></path>
				</svg>
				<p>{shippingInformationHeadingText}</p>
				<svg
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M11 5.5L7 9.5L3 5.5"
						stroke="currentColor"
						strokeWidth="var(--icon-stroke-width)"
						strokeLinecap="round"
						strokeLinejoin="round"
					></path>
				</svg>
			</div>
			<div>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
					<path d="M11.571 1.059c0-.309.249-.559.556-.559h1.33C17.07.5 20 3.453 20 7.09s-2.93 6.589-6.543 6.589L1.9 13.71l-.591-.59.59-.572 11.558.013c2.997 0 5.432-2.451 5.432-5.472 0-3.02-2.435-5.471-5.432-5.471h-1.33a.557.557 0 0 1-.556-.56"></path>
					<path d="M6.003 7.007a.553.553 0 0 1 .786.011.56.56 0 0 1-.012.79zM1.31 13.12l5.43 5.426a.56.56 0 0 1 0 .79.553.553 0 0 1-.785 0L.162 13.503a.56.56 0 0 1 .007-.796l3.035-2.965 2.8-2.735.773.801-2.798 2.736c-1.025 1-2.108 2.027-2.67 2.576"></path>
				</svg>
				<p>{returnsHeadingText}</p>
				<svg
					viewBox="0 0 14 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M11 5.5L7 9.5L3 5.5"
						stroke="currentColor"
						strokeWidth="var(--icon-stroke-width)"
						strokeLinecap="round"
						strokeLinejoin="round"
					></path>
				</svg>
			</div>
		</div>
	)
}

const BenefitsMarquee = ({
	returnsText = "",
	shippingInformationText = "",
	supportText = "",
}) => {
	return (
		<div className={css.benefits_marquee}>
			<div className={css.benefits_marquee_track}>
				{Array.from({ length: 3 }).map((_, index) => (
					<div key={index} className={css.benefits_marquee_group}>
						<div className={css.benefits_marquee_item}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path d="m16.412 2.079-.082.055c-.178.122-.358.244-.546.389l-.007.005c-.941.702-2.173 1.743-3.495 2.968a.5.5 0 0 1-.448.12L3.686 3.81a1 1 0 0 0-.147-.014.55.55 0 0 0-.509.333.56.56 0 0 0 .206.682l.005.004L8.614 8.3a.5.5 0 0 1 .081.773L8.67 9.1c-1.289 1.38-2.36 2.636-3.082 3.614a9 9 0 0 0-.31.437.5.5 0 0 1-.527.218l-2.553-.564a1 1 0 0 0-.147-.014.55.55 0 0 0-.51.333.56.56 0 0 0 .17.657l2.999 1.106a.5.5 0 0 1 .292.285l1.23 3.108a.54.54 0 0 0 .482.218.5.5 0 0 1 .136-.085.56.56 0 0 0 .328-.634v-.002l-.57-2.566a.5.5 0 0 1 .219-.529c.081-.052.144-.098.219-.153.062-.045.131-.096.226-.16.988-.72 2.233-1.785 3.616-3.07l.025-.026a.5.5 0 0 1 .772.081l3.498 5.366a.54.54 0 0 0 .464.253.56.56 0 0 0 .546-.679l-1.815-8.15a.5.5 0 0 1 .12-.447c1.222-1.324 2.272-2.54 2.962-3.485l.009-.01c.145-.188.267-.367.389-.545l.055-.081c.185-.282.42-.647.53-1.021.105-.36.073-.63-.15-.854a.74.74 0 0 0-.524-.201c-.28 0-.663.12-1.357.579m-5.24 10.319c-1.243 1.138-2.38 2.101-3.317 2.783l-.011.008-.168.119-.019.014.499 2.247a1.56 1.56 0 0 1-.832 1.726c-.221.18-.496.205-.65.205-.51 0-1.011-.248-1.309-.716a.5.5 0 0 1-.042-.084l-1.169-2.952-2.847-1.05a.5.5 0 0 1-.094-.047c-.65-.41-.872-1.22-.598-1.904l.004-.008a1.554 1.554 0 0 1 1.7-.927l.026.004.046.008 2.247.496.138-.19.007-.009c.682-.923 1.646-2.064 2.787-3.306l-4.871-3.16c-.648-.412-.869-1.22-.595-1.903l.003-.008a1.554 1.554 0 0 1 1.7-.926q.014 0 .026.003l.046.008 7.916 1.755c1.265-1.162 2.45-2.16 3.381-2.855a12 12 0 0 1 .675-.478l.006-.005C16.597.758 17.185.5 17.77.5c.46 0 .907.172 1.23.494.578.577.567 1.285.403 1.843-.155.529-.468 1.006-.645 1.275l-.016.025-.055.081c-.12.176-.26.379-.422.59-.69.944-1.701 2.118-2.853 3.374l1.76 7.902a1.56 1.56 0 0 1-.918 1.765l-.002.001a1.6 1.6 0 0 1-.604.123c-.508 0-1.01-.247-1.307-.714z"></path>
							</svg>
							<p>{shippingInformationText}</p>
						</div>

						<div className={css.benefits_marquee_item}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path d="M5.8 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0M9.23 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0M12.66 10.243a1 1 0 1 1 2 0 1 1 0 0 1-2 0"></path>
								<path
									fillRule="evenodd"
									d="m16.645 17.204-.008-.004a19 19 0 0 1-.767-.423c-.645-.367-1.263-.72-1.442-.598a7.472 7.472 0 1 1 2.05-2.082c-.134.202.192.843.533 1.513.147.29.297.584.414.85l.006.014c.193.443.293.808.136.944-.128.136-.488.006-.922-.214m1.614.936c-.38.372-.857.345-1.046.321a2.5 2.5 0 0 1-.626-.181 11 11 0 0 1-1.081-.56l-.132-.074c-.29-.166-.538-.307-.744-.408a8.472 8.472 0 1 1 2.877-2.902l.008.02c.092.22.226.483.385.795l.084.165c.178.353.388.774.516 1.146.063.181.131.42.143.669.01.225-.02.668-.384 1.009"
								></path>
							</svg>
							<p>{supportText}</p>
						</div>

						<div className={css.benefits_marquee_item}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path d="M11.571 1.059c0-.309.249-.559.556-.559h1.33C17.07.5 20 3.453 20 7.09s-2.93 6.589-6.543 6.589L1.9 13.71l-.591-.59.59-.572 11.558.013c2.997 0 5.432-2.451 5.432-5.472 0-3.02-2.435-5.471-5.432-5.471h-1.33a.557.557 0 0 1-.556-.56"></path>
								<path d="M6.003 7.007a.553.553 0 0 1 .786.011.56.56 0 0 1-.012.79zM1.31 13.12l5.43 5.426a.56.56 0 0 1 0 .79.553.553 0 0 1-.785 0L.162 13.503a.56.56 0 0 1 .007-.796l3.035-2.965 2.8-2.735.773.801-2.798 2.736c-1.025 1-2.108 2.027-2.67 2.576"></path>
							</svg>
							<p>{returnsText}</p>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

const ImageWithDetailBulletPointsSection = ({
	imageUrl,
	headingText = "",
	benefits = [],
}) => {
	return (
		<div className={css.image_with_detail_bullet_points_section}>
			<div className={css.image_with_detail_bullet_points_section_image}>
				<img src={imageUrl} />
			</div>

			<div
				className={
					css.image_with_detail_bullet_points_section_details_container
				}
			>
				<h2
					className={
						css.image_with_detail_bullet_points_section_details_title
					}
				>
					{headingText}
				</h2>

				{benefits?.map((benefit, i) => {
					return (
						<div
							key={`benefit-${i}`}
							className={
								css.image_with_detail_bullet_points_section_details_bullet_point
							}
						>
							<svg
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="m12 22.75c-5.93 0-10.75-4.82-10.75-10.75s4.82-10.75 10.75-10.75 10.75 4.82 10.75 10.75-4.82 10.75-10.75 10.75zm0-20c-5.1 0-9.25 4.15-9.25 9.25s4.15 9.25 9.25 9.25 9.25-4.15 9.25-9.25-4.15-9.25-9.25-9.25z"></path>
								<path d="m10.58 15.58c-0.2 0-0.39-0.08-0.53-0.22l-2.83-2.83c-0.29-0.29-0.29-0.77 0-1.06s0.77-0.29 1.06 0l2.3 2.3 5.14-5.14c0.29-0.29 0.77-0.29 1.06 0s0.29 0.77 0 1.06l-5.67 5.67c-0.14 0.14-0.33 0.22-0.53 0.22z"></path>
							</svg>
							<div>
								<h3>{benefit.heading || ""}</h3>
								<p>{benefit.content || ""}</p>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}

const PurchaseReasonsSection = ({ headingText = "", reasons = [] }) => {
	return (
		<div className={css.purchase_reasons_section}>
			<h2>{renderGradientBracketText(headingText)}</h2>

			<div className={css.purchase_reasons_section_reasons_grid}>
				{reasons?.map((reason, i) => {
					return (
						<div
							key={`reason-${i}`}
							className={css.purchase_reasons_section_reason_item}
						>
							<h3>{reason.heading || ""}</h3>
							<p>{reason.content || ""}</p>
						</div>
					)
				})}
			</div>
		</div>
	)
}

const ComparisonTableSection = ({
	storeName = "",
	headingText = "",
	contentText = "",
	items = [],
	othersText = "",
}) => {
	return (
		<div className={css.comparison_table_section}>
			<h2>{renderGradientBracketText(headingText)}</h2>

			<p>{contentText}</p>

			<table className={css.comparison_table}>
				<thead>
					<tr>
						<th>&nbsp;</th>
						<th
							align="center"
							className={css.comparison_table_logo}
						>
							{storeName}
						</th>
						<th
							align="center"
							className={css.comparison_table_others}
						>
							{othersText}
						</th>
					</tr>
				</thead>
				<tbody>
					{items.map((item, i) => {
						return (
							<tr key={`item-${i}`}>
								<td
									align="center"
									className={css.comparison_table_row_name}
								>
									<h3>{item || ""}</h3>
								</td>
								<td
									align="center"
									className={
										css.comparison_table_checkmark_container
									}
								>
									<svg
										className={
											css.comparison_table_checkmark_icon
										}
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 25.45 25.43"
									>
										<polygon points="25.45 0 24.06 0 9.5 15.27 2.12 10.24 0 12.55 9.28 25.43 25.45 2.48 25.45 0"></polygon>
									</svg>
								</td>
								<td
									align="center"
									className={css.comparison_table_x_container}
								>
									<div
										className={css.comparison_table_x_icon}
									>
										<span
											className={
												css.comparison_table_x_icon_line
											}
										></span>
										<span
											className={
												css.comparison_table_x_icon_line
											}
										></span>
									</div>
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

const FaqSection = ({ headingText = "", questions = [] }) => {
	return (
		<div className={css.faq_section}>
			<h2>{headingText}</h2>

			{questions?.map((question, i) => {
				return (
					<div
						key={`question-${i}`}
						className={css.faq_section_question}
					>
						<p>{question.question || ""}</p>
						<svg
							viewBox="0 0 14 14"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M11 5.5L7 9.5L3 5.5"
								stroke="currentColor"
								strokeWidth="var(--icon-stroke-width)"
								strokeLinecap="round"
								strokeLinejoin="round"
							></path>
						</svg>
					</div>
				)
			})}
		</div>
	)
}

const StarRating = ({ rating = 5, maxRating = 5, size = 18 }) => {
	const ratingPercent = (rating / maxRating) * 100

	return (
		<div
			className={css.star_rating_container}
			style={{
				"--rating": `${ratingPercent}%`,
				"--star-size": `calc(${size}px * var(--preview-base-size))`,
			}}
		>
			{Array.from({ length: maxRating }).map((_, index) => (
				<div
					key={index}
					className={css.star_rating_icon_container}
					style={{ "--star-index": index }}
				>
					<svg
						className={css.star_rating_icon}
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 1920 1920"
					>
						<path
							d="M1915.918 737.475c-10.955-33.543-42.014-56.131-77.364-56.131h-612.029l-189.063-582.1v-.112C1026.394 65.588 995.335 43 959.984 43c-35.237 0-66.41 22.588-77.365 56.245L693.443 681.344H81.415c-35.35 0-66.41 22.588-77.365 56.131-10.955 33.544.79 70.137 29.478 91.03l495.247 359.831-189.177 582.212c-10.955 33.657 1.13 70.25 29.817 90.918 14.23 10.278 30.946 15.487 47.66 15.487 16.716 0 33.432-5.21 47.775-15.6l495.134-359.718 495.021 359.718c28.574 20.781 67.087 20.781 95.662.113 28.687-20.668 40.658-57.261 29.703-91.03l-189.176-582.1 495.36-359.83c28.574-20.894 40.433-57.487 29.364-91.03"
							fillRule="evenodd"
						/>
					</svg>
				</div>
			))}
		</div>
	)
}

const ReviewsSection = ({
	reviews,
	avgRating,
	mainImageUrl,
	productTitle,
	basedOnText,
	reviewCount,
	headingText,
	verifiedBuyerText,
	voteText,
}) => {
	return (
		<div className={css.reviews_section}>
			<h2>{headingText}</h2>
			<div className={css.reviews_section_rating_number}>{avgRating}</div>
			<div className={css.reviews_section_rating_container}>
				<StarRating rating={avgRating} />
			</div>
			<p className={css.reviews_section_review_count}>
				{basedOnText.replace("<amount>", reviewCount)}
			</p>

			{reviews?.map((review, i) => (
				<div
					key={`review-${i}`}
					className={css.reviews_section_review_item}
				>
					<div className={css.reviews_section_review_item_header}>
						<p
							className={
								css.reviews_section_review_item_customer_name
							}
						>
							{review.authorName}
						</p>
						<p className={css.reviews_section_review_item_date}>
							{review.date}
						</p>
					</div>
					<div className={css.reviews_section_review_item_rating}>
						<StarRating rating={review.stars} size={10} />
					</div>
					<div
						className={
							css.reviews_section_review_item_verified_buyer
						}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 14 14"
						>
							<path d="M6.506.28a1.49 1.49 0 011.806.051l.107.096.808.793c.137.134.307.226.492.267l.141.021 1.118.081a1.49 1.49 0 011.353 1.214l.019.134.109 1.125c.018.2.095.39.22.545l.08.09.808.792c.474.475.57 1.202.249 1.779l-.075.121-.627.936a1.022 1.022 0 00-.177.532l.004.143.109 1.13a1.492 1.492 0 01-.949 1.54l-.136.045-1.088.3a1.018 1.018 0 00-.494.312l-.073.094-.633.932a1.49 1.49 0 01-1.715.57l-.133-.053-1.029-.468a1.019 1.019 0 00-.554-.082l-.14.028-1.094.317c-.65.18-1.332-.1-1.674-.667l-.068-.126-.486-1.027a1.019 1.019 0 00-.394-.433l-.106-.055-1.029-.468a1.494 1.494 0 01-.858-1.588l.029-.14.283-1.1A1.01 1.01 0 00.693 7.5l-.052-.133-.493-1.019A1.493 1.493 0 01.51 4.576l.113-.089.918-.65c.165-.117.291-.28.363-.467l.037-.115.287-1.097a1.49 1.49 0 011.418-1.121l.144.004 1.124.084c.19.015.38-.025.548-.112l.122-.075.922-.658zm3.756 5.105c.686-.682-.401-1.526-.947-.98l-.334.344-2.872 3.05-.57-.612-.327-.34-.359-.367-.123-.124a.687.687 0 10-.947.985l.45.46 1.078 1.133.798.851 3.29-3.502.796-.83.067-.068z"></path>
						</svg>
						<p>{verifiedBuyerText}</p>
					</div>
					<p className={css.reviews_section_review_item_review_title}>
						{review.title}
					</p>
					<p
						className={
							css.reviews_section_review_item_review_description
						}
					>
						{review.content}
					</p>
					<div className={css.reviews_section_review_item_product}>
						<img src={mainImageUrl} alt="Review Image" />
						<p>{productTitle}</p>
					</div>
					<div className={css.reviews_section_review_item_helpful}>
						<p>{voteText}</p>
						<div
							className={
								css.reviews_section_review_item_thumbs_up
							}
						>
							<svg
								enableBackground="new 0 0 949.3 949.3"
								version="1.1"
								viewBox="0 0 949.3 949.3"
								xmlSpace="preserve"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="m899.3 338.1-355.3-0.4s20.6-84.3 24.3-123.8c2.5-27 3.101-53.9-3.1-80.5-9.2-40-19.5-65-35.9-96-8.8-16.4-25.8-26.6-44.3-26.6h-68.8c-27.7 0-50.101 22.5-50 50.2 0.2 46.3 0.2 108.1-0.8 110.2-27 56.8-58.3 111.3-93.6 163.3-16.1 23.8-33 46.9-52.6 68-8.6 9.2-13.3 21.4-13.3 33.9v437.2c0 12.898 5 25.398 14 34.699 13.4 13.801 37.4 30.199 76.8 30.199 124.3-0.5 325.7-1.6 468.9-0.6 17.9 0.1 34.5-9.299 43.5-24.799l126.3-216.2c8.6-15 13.1-32 13.199-49.299l0.7-259.3c1e-3 -27.7-22.399-50.1-49.999-50.2z"></path>
								<path d="m94.8 409.5h-44.8c-27.6 0-50 22.4-50 50v429c0 27.602 22.4 50 50 50h44.7c27.6 0 50-22.398 50-50v-429c0.1-27.6-22.3-50-49.9-50z"></path>
							</svg>
							<p>{review.likes}</p>
						</div>
						<div
							className={
								css.reviews_section_review_item_thumbs_down
							}
						>
							<svg
								enableBackground="new 0 0 512 512"
								version="1.1"
								viewBox="0 0 512 512"
								xmlSpace="preserve"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="m495.97 297.23-24.695 14.225c-9.711 5.598-20.727 8.543-31.945 8.543h-71.329c-8.836 0-16 7.164-16 16v132.16c0 15.375-12.469 27.84-27.844 27.84h-0.094c-11.977 0-22.617-7.668-26.406-19.035l-30.719-80.355c-7.117-18.611-19.844-34.547-36.422-45.598l-70.515-47.012v-240h14.273c11.602 0 22.977-3.152 32.93-9.121l49.594-29.758c9.953-5.969 21.328-9.121 32.93-9.121h136.94c13.852 0 27.32 4.492 38.398 12.801l34.141 25.6c8.054 6.042 12.796 15.527 12.796 25.599v189.5c0 11.443-6.109 22.016-16.031 27.728z"></path>
								<path d="m128 304v-240c0-17.672-14.328-32-32-32h-96v304h96c17.672 0 32-14.326 32-32zm-32-208c0 17.672-14.328 32-32 32s-32-14.328-32-32 14.328-32 32-32 32 14.328 32 32z"></path>
							</svg>
							<p>{review.dislikes}</p>
						</div>
					</div>
				</div>
			))}
		</div>
	)
}

const EmailSubscriptionSection = ({
	headingText = "Subscribe to our emails",
	subheadingText = "Be the first to know about new collections and exclusive offers.",
	emailPlaceholderText = "Email",
}) => {
	return (
		<div className={css.email_subscription_section}>
			<div className={css.email_subscription_section_content}>
				<h2>{headingText}</h2>
				<p>{subheadingText}</p>
				<div className={css.email_subscription_section_input}>
					<p>{emailPlaceholderText}</p>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 14 10"
					>
						<path
							fill="currentColor"
							fillRule="evenodd"
							d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546"
							clipRule="evenodd"
						></path>
					</svg>
				</div>
			</div>
		</div>
	)
}

const TrustBadgesSection = ({
	shippingHeading = "Free & Fast Shipping",
	shippingContent = "Doorstep delivery worldwide.",
	returnHeading = "Hassle-free exchange",
	returnContent = "No questions asked",
	priceMatchHeading = "Price-match guarantee",
	priceMatchContent = "Save money when ordering with us.",
	satisfactionHeading = "5 Star Reviews",
	satisfactionContent = "Customer satisfaction guaranteed is our #1 priority",
}) => {
	return (
		<div className={css.trust_badges_section}>
			<div className={css.trust_badges_section_badge}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 32 32"
					version="1.1"
				>
					<path
						d="M29.871 6.026l-13.871-5.1-14.394 5.283v19.642l14.394 5.222 14.394-5.222v-19.634l-0.524-0.192zM15.467 29.746l-12.795-4.642v-17.747l12.795 4.642v17.747zM16 11.058l-12.326-4.472 12.326-4.524 12.315 4.528-12.315 4.468zM29.328 25.104l-12.795 4.642v-17.747l12.795-4.623v17.728z"
						fill="#000000"
					></path>
				</svg>
				<p className={css.trust_badges_section_title}>
					{shippingHeading}
				</p>
				<p className={css.trust_badges_section_description}>
					{shippingContent}
				</p>
			</div>
			<div className={css.trust_badges_section_badge}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="#000000"
					viewBox="0 0 1024 1024"
				>
					<path d="M197.37 181.34c0-11.09-8.99-20.08-20.08-20.08s-20.08 8.99-20.08 20.08v125.49c.012.896.088 1.764.223 2.612l-.013-.102c-60.245 9.912-105.642 61.606-105.642 123.902 0 62.222 45.288 113.866 104.699 123.767l.733.101v293.45c0 11.09 8.99 20.08 20.08 20.08s20.08-8.99 20.08-20.08V557.21c60.122-10.027 105.384-61.662 105.384-123.869 0-62.278-45.366-113.96-104.854-123.801l-.73-.1c.11-.739.182-1.607.2-2.488v-.022zm65.26 252c0 47.132-38.208 85.34-85.34 85.34s-85.34-38.208-85.34-85.34c0-47.132 38.208-85.34 85.34-85.34 47.117.028 85.306 38.214 85.34 85.327v.003zM511.9 161.26c11.085.023 20.066 8.997 20.1 20.077V474.19a20.1 20.1 0 01-.213 2.618l.013-.108c60.228 9.928 105.606 61.613 105.606 123.896 0 62.212-45.275 113.85-104.673 123.763l-.733.101v126.1c0 11.09-8.99 20.08-20.08 20.08s-20.08-8.99-20.08-20.08v-126.2c-60.141-10.005-105.426-61.648-105.426-123.867 0-62.276 45.368-113.957 104.856-123.793l.729-.1a20.478 20.478 0 01-.2-2.487V181.34c.034-11.083 9.015-20.057 20.098-20.08h.002zm0 524.65c47.126 0 85.33-38.204 85.33-85.33s-38.204-85.33-85.33-85.33-85.33 38.204-85.33 85.33c.034 47.113 38.217 85.296 85.327 85.33h.003zm460.3-212.43v-.033c0-62.277-45.39-113.952-104.892-123.748l-.728-.099a18.69 18.69 0 00.21-2.492V181.44c0-11.09-8.99-20.08-20.08-20.08s-20.08 8.99-20.08 20.08v165.65c.014.899.086 1.767.213 2.618l-.013-.108c-60.218 9.942-105.584 61.623-105.584 123.901 0 62.206 45.262 113.841 104.65 123.768l.734.101v253.29c0 11.09 8.99 20.08 20.08 20.08s20.08-8.99 20.08-20.08V597.37c60.135-10.021 105.41-61.664 105.41-123.882v-.009zm-125.49 85.34h-.05c-47.132 0-85.34-38.208-85.34-85.34s38.208-85.34 85.34-85.34c47.132 0 85.34 38.208 85.34 85.34-.028 47.103-38.191 85.283-85.285 85.34h-.005z"></path>
				</svg>
				<p className={css.trust_badges_section_title}>
					{priceMatchHeading}
				</p>
				<p className={css.trust_badges_section_description}>
					{priceMatchContent}
				</p>
			</div>
			<div className={css.trust_badges_section_badge}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="#000000"
					viewBox="0 0 1024 1024"
				>
					<path d="M249.6 627.879c51.252 0 94.075-36.59 101.642-86.128a20.476 20.476 0 0110.015-14.649l1.403-.809a20.49 20.49 0 019.246-2.715c129.082-6.21 228.845-90.344 228.845-189.924 0-103.944-108.374-190.29-244.06-190.29-135.677 0-244.05 86.347-244.05 190.29 0 72.301 52.772 138.239 135.615 170.444l5.211 2.355a20.48 20.48 0 0111.924 18.607c0 38.067-14.927 73.572-40.338 99.877a103.256 103.256 0 0024.548 2.942zm0 40.96c-25.799 0-50.657-6.823-72.477-19.569-4.011-2.04-6.981-4.659-9.087-8.124-5.873-9.666-2.798-22.264 6.868-28.137 26.777-16.27 44.692-43.707 48.682-74.745.558-4.338.844-8.746.844-13.204h20.48l-8.556 18.607-4.076-1.874 8.556-18.607-7.42 19.088a325.149 325.149 0 01-9.828-4.011c-92.051-39.385-151.907-117.145-151.907-204.61 0-128.871 128.495-231.25 285.01-231.25 156.524 0 285.02 102.378 285.02 231.25 0 119.569-110.956 217.066-253.246 229.833-4.828.433-9.693.769-14.591 1.004l-.984-20.456 10.231 17.742-11.633-16.933 20.245 3.092a142.676 142.676 0 01-3.268 15.551c-16.566 61.545-72.858 105.353-138.864 105.353zm145.821-33.679c-3.138-10.867 3.128-22.22 13.994-25.358s22.22 3.128 25.358 13.994c23.262 80.558 112.798 141.479 220.731 146.68a20.486 20.486 0 019.26 2.723l1.382.799a20.48 20.48 0 019.999 14.64c7.485 48.997 49.848 85.197 100.546 85.197 18.284 0 35.823-4.817 51.25-13.832 9.766-5.707 22.308-2.416 28.015 7.349s2.416 22.308-7.349 28.015c-21.654 12.654-46.318 19.428-71.916 19.428-65.45 0-121.279-43.416-137.76-104.418-117.957-10.664-216.152-80.475-243.511-175.218z"></path>
					<path d="M850.81 859.119c9.666 5.874 12.74 18.472 6.865 28.138s-18.472 12.74-28.138 6.865c-42.287-25.699-68.673-71.583-68.673-122.001a20.48 20.48 0 0111.931-18.61l4.035-1.853 1.128-.478c82.025-31.89 134.274-97.174 134.274-168.734 0-93.259-88.667-173.58-208.08-186.57-11.244-1.223-19.368-11.33-18.145-22.575s11.33-19.368 22.575-18.145c138.63 15.08 244.61 111.085 244.61 227.29 0 86.708-59.309 163.8-150.515 202.876 3.983 30.645 21.688 57.725 48.133 73.797z"></path>
				</svg>
				<p className={css.trust_badges_section_title}>
					{returnHeading}
				</p>
				<p className={css.trust_badges_section_description}>
					{returnContent}
				</p>
			</div>
			<div className={css.trust_badges_section_badge}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 24 24"
					fill="none"
				>
					<path
						d="M14.65 8.93274L12.4852 4.30901C12.2923 3.89699 11.7077 3.897 11.5148 4.30902L9.35002 8.93274L4.45559 9.68243C4.02435 9.74848 3.84827 10.2758 4.15292 10.5888L7.71225 14.2461L6.87774 19.3749C6.80571 19.8176 7.27445 20.1487 7.66601 19.9317L12 17.5299L16.334 19.9317C16.7256 20.1487 17.1943 19.8176 17.1223 19.3749L16.2878 14.2461L19.8471 10.5888C20.1517 10.2758 19.9756 9.74848 19.5444 9.68243L14.65 8.93274Z"
						stroke="#000000"
						strokeLinecap="round"
						strokeLinejoin="round"
					></path>
				</svg>
				<p className={css.trust_badges_section_title}>
					{satisfactionHeading}
				</p>
				<p className={css.trust_badges_section_description}>
					{satisfactionContent}
				</p>
			</div>
		</div>
	)
}

const FooterSection = ({
	storeName,
	customerSupportHeading = "Need To Get In Touch?",
	customerSupportContent = "",
}) => {
	const { t } = useTranslation()
	// Default content if none provided - use translated text
	const defaultContent = `<p>${t("components.kopy_pages_preview.customer_support_default")}</p>`
	const contentToRender = customerSupportContent || defaultContent

	return (
		<div className={css.footer_section}>
			<div className={css.footer_section_waves_animated_container}>
				<svg
					className={css.footer_section_waves_animated}
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 24 150 28"
					preserveAspectRatio="none"
				>
					<defs>
						<path
							id="gentle-wave"
							d="M-160 44c30 0
								58-18 88-18s
								58 18 88 18
								58-18 88-18
								58 18 88 18
								v44h-352z"
						></path>
					</defs>
					<g className={css.footer_section_waves_animated_parallax1}>
						<use
							xlinkHref="#gentle-wave"
							x="50"
							y="3"
							fill="currentColor"
						></use>
					</g>
					<g className={css.footer_section_waves_animated_parallax2}>
						<use
							xlinkHref="#gentle-wave"
							x="50"
							y="0"
							fill="currentColor"
						></use>
					</g>
					<g className={css.footer_section_waves_animated_parallax3}>
						<use
							xlinkHref="#gentle-wave"
							x="50"
							y="9"
							fill="currentColor"
						></use>
					</g>
					<g className={css.footer_section_waves_animated_parallax4}>
						<use
							xlinkHref="#gentle-wave"
							x="50"
							y="6"
							fill="currentColor"
						></use>
					</g>
				</svg>
			</div>

			<div className={css.footer_section_store_info_container}>
				<p className={css.footer_section_contact_info_title}>
					{customerSupportHeading}
				</p>

				<div
					className={css.footer_section_contact_info_description}
					dangerouslySetInnerHTML={{
						__html: DOMPurify.sanitize(contentToRender),
					}}
				/>
			</div>

			<div className={css.footer_section_ending_container}>
				{/* <div
					className={
						css.footer_section_ending_currency_language_container
					}
				>
					<div
						className={css.footer_section_ending_currency_container}
					>
						<p
							className={
								css.footer_section_ending_currency_language_title
							}
						>
							Country/region
						</p>
						<div
							className={
								css.footer_section_ending_currency_language_value
							}
						>
							<span>United States | USD $</span>
							<svg
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11 5.5L7 9.5L3 5.5"
									stroke="currentColor"
									strokeWidth="var(--icon-stroke-width)"
									strokeLinecap="round"
									strokeLinejoin="round"
								></path>
							</svg>
						</div>
					</div>
					<div
						className={css.footer_section_ending_language_container}
					>
						<p
							className={
								css.footer_section_ending_currency_language_title
							}
						>
							Language
						</p>
						<div
							className={
								css.footer_section_ending_currency_language_value
							}
						>
							<span>English</span>
							<svg
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11 5.5L7 9.5L3 5.5"
									stroke="currentColor"
									strokeWidth="var(--icon-stroke-width)"
									strokeLinecap="round"
									strokeLinejoin="round"
								></path>
							</svg>
						</div>
					</div>
				</div>

				<div className={css.footer_section_ending_copyright_container}>
					<p className={css.footer_section_ending_copyright_content}>
						© 2025, {storeName}
					</p>
					<p className={css.footer_section_ending_copyright_content}>
						• Refund policy
					</p>

					<p className={css.footer_section_ending_copyright_content}>
						• Privacy policy
					</p>
					<p className={css.footer_section_ending_copyright_content}>
						• Terms of service
					</p>
					<p className={css.footer_section_ending_copyright_content}>
						• Shipping policy
					</p>
					<p className={css.footer_section_ending_copyright_content}>
						• Contact information
					</p>
				</div> */}

				<ul
					style={{
						display: "flex",
						justifyContent: "center",
						gap: "5px",
						paddingLeft: "0",
					}}
				>
					<li
						style={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
						}}
					>
						<svg
							viewBox="0 0 38 24"
							xmlns="http://www.w3.org/2000/svg"
							width="34"
							height="20"
							role="img"
							aria-labelledby="pi-paypal"
						>
							<title id="pi-paypal">PayPal</title>
							<path
								opacity=".07"
								d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z"
							></path>
							<path
								fill="#fff"
								d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"
							></path>
							<path
								fill="#003087"
								d="M23.9 8.3c.2-1 0-1.7-.6-2.3-.6-.7-1.7-1-3.1-1h-4.1c-.3 0-.5.2-.6.5L14 15.6c0 .2.1.4.3.4H17l.4-3.4 1.8-2.2 4.7-2.1z"
							></path>
							<path
								fill="#3086C8"
								d="M23.9 8.3l-.2.2c-.5 2.8-2.2 3.8-4.6 3.8H18c-.3 0-.5.2-.6.5l-.6 3.9-.2 1c0 .2.1.4.3.4H19c.3 0 .5-.2.5-.4v-.1l.4-2.4v-.1c0-.2.3-.4.5-.4h.3c2.1 0 3.7-.8 4.1-3.2.2-1 .1-1.8-.4-2.4-.1-.5-.3-.7-.5-.8z"
							></path>
							<path
								fill="#012169"
								d="M23.3 8.1c-.1-.1-.2-.1-.3-.1-.1 0-.2 0-.3-.1-.3-.1-.7-.1-1.1-.1h-3c-.1 0-.2 0-.2.1-.2.1-.3.2-.3.4l-.7 4.4v.1c0-.3.3-.5.6-.5h1.3c2.5 0 4.1-1 4.6-3.8v-.2c-.1-.1-.3-.2-.5-.2h-.1z"
							></path>
						</svg>
					</li>

					<li
						style={{
							display: "flex",
							alignItems: "center",
							gap: "10px",
						}}
					>
						<svg
							viewBox="0 0 38 24"
							width="34"
							height="20"
							xmlns="http://www.w3.org/2000/svg"
							role="img"
							aria-labelledby="pi-venmo"
						>
							<title id="pi-venmo">Venmo</title>
							<g fill="none" fillRule="evenodd">
								<rect
									fillOpacity=".07"
									fill="#000"
									width="38"
									height="24"
									rx="3"
								></rect>
								<path
									fill="#3D95CE"
									d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32"
								></path>
								<path
									d="M24.675 8.36c0 3.064-2.557 7.045-4.633 9.84h-4.74L13.4 6.57l4.151-.402 1.005 8.275c.94-1.566 2.099-4.025 2.099-5.702 0-.918-.154-1.543-.394-2.058l3.78-.783c.437.738.634 1.499.634 2.46z"
									fill="#FFF"
									fillRule="nonzero"
								></path>
							</g>
						</svg>
					</li>
				</ul>
			</div>
		</div>
	)
}
